// @ts-ignore: No TS declaration present for the timezone data bundle.
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js'

import { getGarageParkingPeriod } from '@/garage-parking-period'

import {
    getReservationContactInfoValues,
    isContactInfoEmpty,
} from '@/components/checkout/contact-info/utils'
import {
    getCheckoutPriceBreakdown,
    getReservationDuration,
} from '@/components/checkout/order-summary/utils'
import { isVehicleInfoEmpty } from '@/components/checkout/vehicle-info/utils'
import type {
    ActiveReservationResponse,
    CheckoutDisclaimerLinks,
    CheckoutContactInfoValues,
    CheckoutSummaryProps,
    CheckoutVehicleInfoValues,
    GetActiveReservationPayload,
} from '@/types/reservation-checkout'
import type { CheckoutMode } from './types'

export type AllocatedCheckoutReservation = ActiveReservationResponse & {
    garage: NonNullable<ActiveReservationResponse['garage']>
}

export type CheckoutViewModel = {
    reservationContactInfoValues: CheckoutContactInfoValues
    invoiceRequired: boolean
    vehicleInfoValues: CheckoutVehicleInfoValues
    disclaimerLinks: CheckoutDisclaimerLinks
    summaryProps: CheckoutSummaryProps
    currentUserCars: AllocatedCheckoutReservation['current_user_cars']
    prefillCheckoutInfo: boolean
    phoneCountryBillingHint: string | null
    currentlyAppliedCoupon: AllocatedCheckoutReservation['currently_applied_coupon']
    availableBenefitCodes: AllocatedCheckoutReservation['available_benefit_codes']
    sendSms: boolean
    alreadyKnowGarage: boolean
    checkoutMode: CheckoutMode
    beginCheckoutTracking: AllocatedCheckoutReservation['begin_checkout_tracking']
}

export function hasAllocatedCheckoutReservation(
    data: GetActiveReservationPayload | undefined,
): data is AllocatedCheckoutReservation {
    return (
        !!data &&
        data.reservation.state === 'allocated' &&
        data.garage !== null &&
        'checkout_amount_cents' in data.reservation
    )
}

export function buildCheckoutViewModel(data: AllocatedCheckoutReservation): CheckoutViewModel {
    const { reservation, garage } = data
    const { days, hours } = getReservationDuration(reservation.begins_at, reservation.ends_at)
    const beginsAt = moment.tz(reservation.begins_at, 'Europe/Prague')
    const endsAt = moment.tz(reservation.ends_at, 'Europe/Prague')
    const requestedBeginsAt = moment.tz(reservation.requested_begins_at ?? reservation.begins_at, 'Europe/Prague')
    const requestedEndsAt = moment.tz(reservation.requested_ends_at ?? reservation.ends_at, 'Europe/Prague')
    const parkingPeriod = reservation.minimum_stay_applied === true &&
        requestedBeginsAt.isValid() && requestedEndsAt.isAfter(requestedBeginsAt) &&
        beginsAt.isSame(requestedBeginsAt) && endsAt.isAfter(requestedEndsAt)
        ? getGarageParkingPeriod({ beginsAt: beginsAt.toISOString(), endsAt: endsAt.toISOString(), minimumStayApplied: true })
        : null
    const priceBreakdown = getCheckoutPriceBreakdown({
        amountCents: reservation.amount_cents,
        checkoutAmountCents: reservation.checkout_amount_cents,
        coupon: data.currently_applied_coupon,
    })
    const reservationContactInfoValues = getReservationContactInfoValues(reservation)
    const prefillCheckoutInfo = data.current_user?.prefill_checkout_info ?? true
    const phoneCountryBillingHint = getCheckoutPhoneCountryBillingHint({
        reservationRequestedInvoice: reservation.requested_invoice,
        reservationInvoiceCountry: reservation.invoice_country,
        profileInvoiceCountry: data.current_user?.invoice_country,
        shouldPrefillCheckoutInfo: prefillCheckoutInfo,
    })
    const vehicleInfoValues = {
        number_plate: reservation.number_plate ?? '',
        car_color: reservation.car_color ?? '',
    }
    const checkoutMode: CheckoutMode = (
        data.current_user === null
        && data.current_user_cars.length === 0
        && isContactInfoEmpty(reservationContactInfoValues)
        && isVehicleInfoEmpty(vehicleInfoValues)
    )
        ? 'single_submit'
        : 'sectioned'

    return {
        reservationContactInfoValues,
        invoiceRequired: reservation.invoice_required,
        vehicleInfoValues,
        disclaimerLinks: data.checkout_disclaimer,
        summaryProps: {
            garageName: garage.name,
            garageImageUrl: garage.image_url,
            owner: data.owner,
            beginsAtDateLabel: beginsAt.format('D.M.YYYY'),
            beginsAtTimeLabel: beginsAt.format('HH:mm'),
            endsAtDateLabel: endsAt.format('D.M.YYYY'),
            endsAtTimeLabel: endsAt.format('HH:mm'),
            reservationDays: days,
            reservationHours: hours,
            originalPrice: priceBreakdown.discountAmount > 0 ? priceBreakdown.originalPrice : undefined,
            discountAmount: priceBreakdown.discountAmount > 0 ? priceBreakdown.discountAmount : undefined,
            finalPrice: priceBreakdown.finalPrice,
            expiredAt: reservation.expired_at,
            minimumStay: parkingPeriod ? {
                requestedEndsAtDateLabel: requestedEndsAt.format('D.M.YYYY'),
                requestedEndsAtTimeLabel: requestedEndsAt.format('HH:mm'),
                parkingPeriod,
            } : undefined,
        },
        currentUserCars: data.current_user_cars,
        prefillCheckoutInfo,
        phoneCountryBillingHint,
        currentlyAppliedCoupon: data.currently_applied_coupon,
        availableBenefitCodes: data.available_benefit_codes,
        sendSms: reservation.send_sms,
        alreadyKnowGarage: reservation.already_know_garage,
        checkoutMode,
        beginCheckoutTracking: data.begin_checkout_tracking,
    }
}

export function getCheckoutPhoneCountryBillingHint({
    reservationRequestedInvoice,
    reservationInvoiceCountry,
    profileInvoiceCountry,
    shouldPrefillCheckoutInfo,
}: {
    reservationRequestedInvoice: boolean
    reservationInvoiceCountry?: string | null
    profileInvoiceCountry?: string | null
    shouldPrefillCheckoutInfo: boolean
}): string | null {
    if (reservationRequestedInvoice && reservationInvoiceCountry?.trim()) {
        return reservationInvoiceCountry
    }

    if (shouldPrefillCheckoutInfo && profileInvoiceCountry?.trim()) {
        return profileInvoiceCountry
    }

    return null
}
